import { useSelector } from 'react-redux'
import getIntlObject from '../selectors/intl'

/**
 * @description This hook transforms a translation map to the respective translated strings.
 * For parameterized translations it returns a function that needs to be called with a
 * parameter object and will return the translated and subtituted string.
 *
 * @param {Object.<string, {stringCode: string, fallback: string, parameters?: string[]}>} translationMap -
 * a record listing all required translations and their defintion object.
 * @param {Object.<string, string>} componentProps - the componentProps with possible overloads for the translations.
 *
 * @returns {Object.<string, string|(params: Object.<string, any>) => string>} an Object that maps each key
 * of the translationMap to the translated string. If the translation is parameterized it will return a function
 * that returns the translated strings and substitutes the included parameters with the parameterMap provided as
 * argument.
 */
export function useTranslationMap(translationMap = {}, componentProps = {}) {
    const intl = useSelector(getIntlObject)
    const translations = Object.keys(translationMap).reduce((translations, prop) => {
        if (componentProps[prop]) {
            translations[prop] = componentProps[prop]
        } else if (!translationMap[prop].parameters) {
            translations[prop] = intl.translate(translationMap[prop].stringCode, {}, translationMap[prop].fallback)
        } else {
            translations[prop] = (params = {}) => {
                if (translationMap[prop].parameters.some(parameter => params[parameter] === undefined)) {
                    console.warn(`Not all parameters are defined for translation ${translationMap[prop].stringCode}`)
                }
                return intl.translate(translationMap[prop].stringCode, params, translationMap[prop].fallback)
            }
        }
        return translations
    }, {})

    return translations
}
